import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography
} from '@mui/material';
import { apiError } from '../../Documents/format';

interface Props {
  open: boolean;
  title: string;
  /** Одна фраза над списком */
  lead?: React.ReactNode;
  /** Что будет сделано, по пунктам */
  items: React.ReactNode[];
  confirmLabel: string;
  onClose: () => void;
  /** Выполняет операцию; ошибка (throw) остаётся в диалоге, оператор может повторить */
  onSubmit: () => Promise<void>;
}

const BORDER = '#e2e8f0';

/** Подтверждение операции без причины: показывает, что будет сделано, и две кнопки - «Отмена» и действие */
const ConfirmDialog: React.FC<Props> = ({ open, title, lead, items, confirmLabel, onClose, onSubmit }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const submit = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit();
      onClose();
    } catch (err: any) {
      setError(apiError(err, 'Не удалось выполнить операцию'));
    } finally {
      setSubmitting(false);
    }
  }, [onSubmit, onClose]);

  return (
    <Dialog
      open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 20px 48px rgba(15, 23, 42, 0.16)' } }}
    >
      <DialogTitle sx={{ py: 1.5, px: 2.5, bgcolor: '#fff', borderBottom: `1px solid ${BORDER}` }}>
        <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {lead && <Typography variant="body2" component="div" sx={{ color: '#475569' }}>{lead}</Typography>}
          <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {items.map((item, i) => (
              <Typography key={i} variant="body2" component="li" sx={{ color: '#334155' }}>{item}</Typography>
            ))}
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ py: 1.5, px: 2.5, borderTop: `1px solid ${BORDER}` }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none', fontWeight: 600 }}>Отмена</Button>
        <Button
          variant="contained" onClick={submit} disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
